import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Loader2, UploadCloud, CheckCircle2, AlertTriangle, ArrowLeft, Download,
  FileSpreadsheet, FileText, Save, FolderInput,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const FIELD_LABELS = {
  sku: "SKU",
  name: "Name / Description",
  category: "Category",
  quantity: "Quantity",
  par: "PAR / Min stock",
  cost: "Cost",
  price: "Price",
  ignore: "— Ignore —",
};

export default function ImportInventoryModal({
  open,
  onOpenChange,
  onImported,
  isStoreAccount = false,
  storeLocations = [],
  currentLocationId = "",
}) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultCategory, setDefaultCategory] = useState("");
  const [importLocationId, setImportLocationId] = useState(currentLocationId || "");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [showNumbersHelp, setShowNumbersHelp] = useState(false);

  // If the caller's current location filter changes while the modal is open, sync
  useEffect(() => {
    if (open) setImportLocationId(currentLocationId || "");
  }, [open, currentLocationId]);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setMapping({});
    setDefaultCategory("");
    setImportLocationId(currentLocationId || "");
    setResult(null);
    setShowNumbersHelp(false);
  };
  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 250);
  };

  const token = () => localStorage.getItem("token");
  const authedAxios = () => axios.create({
    baseURL: API, headers: { Authorization: `Bearer ${token()}` },
  });

  const handleFileChosen = (f) => {
    if (!f) {
      setFile(null);
      setShowNumbersHelp(false);
      return;
    }
    // Client-side guard: Apple Numbers files — show inline instructions
    // instead of uploading to a server that will reject them anyway.
    if (/\.numbers$/i.test(f.name)) {
      setFile(null);
      setShowNumbersHelp(true);
      return;
    }
    setShowNumbersHelp(false);
    setFile(f);
  };

  const handleParse = async () => {
    if (!file) {
      toast.error("Please choose a CSV file");
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authedAxios().post("/inventory/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep("map");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Server-side Numbers-file response → show inline instructions
      if (detail && typeof detail === "object" && detail.error_code === "NUMBERS_FILE") {
        setShowNumbersHelp(true);
        setFile(null);
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
          detail?.message ||
          "We couldn't read this file. Please make sure it's a valid CSV.",
        );
      }
    } finally {
      setParsing(false);
    }
  };

  const updateMapping = (header, field) => {
    setMapping((prev) => ({ ...prev, [header]: field }));
  };

  const canImport = () => {
    const vals = Object.values(mapping || {});
    return vals.includes("sku") || vals.includes("name");
  };

  const handleExecute = async () => {
    if (!preview || !canImport()) {
      toast.error("Map at least SKU or Name/Description");
      return;
    }
    if (isStoreAccount && !importLocationId) {
      toast.error("Please select a location for this import");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping_json", JSON.stringify(mapping));
      fd.append("default_category", defaultCategory || "");
      if (importLocationId) fd.append("location_id", importLocationId);
      const { data } = await authedAxios().post("/inventory/import/execute", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStep("done");
      if (typeof onImported === "function") onImported(data);
      toast.success(`Imported ${data.created + data.updated} items`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const csv = "Row,Reason\n" + result.errors.map((e) => `${e.row},"${(e.reason || "").replace(/"/g, '""')}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "import_errors.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="import-inventory-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import Inventory from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file. We'll match your columns and let you preview before importing.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <UploadStep
            file={file}
            setFile={handleFileChosen}
            showNumbersHelp={showNumbersHelp}
            onDismissNumbersHelp={() => setShowNumbersHelp(false)}
          />
        )}

        {step === "map" && preview && (
          <MapStep
            preview={preview}
            mapping={mapping}
            updateMapping={updateMapping}
            defaultCategory={defaultCategory}
            setDefaultCategory={setDefaultCategory}
            isStoreAccount={isStoreAccount}
            storeLocations={storeLocations}
            importLocationId={importLocationId}
            setImportLocationId={setImportLocationId}
          />
        )}

        {step === "done" && result && (
          <DoneStep result={result} onDownloadErrors={downloadErrors} />
        )}

        <DialogFooter className="mt-2 flex-wrap gap-2">
          {step === "upload" && (
            <>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button className="btn-primary" onClick={handleParse} disabled={!file || parsing} data-testid="import-parse-btn">
                {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
                Continue
              </Button>
            </>
          )}
          {step === "map" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")} data-testid="import-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={handleExecute}
                disabled={
                  importing ||
                  !canImport() ||
                  (isStoreAccount && !importLocationId)
                }
                data-testid="import-execute-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Import {preview?.total_rows} rows
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className="btn-primary" onClick={close} data-testid="import-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadStep({ file, setFile, showNumbersHelp, onDismissNumbersHelp }) {
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const downloadTemplate = () => {
    const csv = [
      "SKU,Description,Category,Quantity,PAR,Cost,Price",
      "EX-001,Example item — edit this row,Keys & Fobs,10,5,25.00,49.99",
      "EX-002,Another sample item,General,0,0,0,0",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "fieldz-inventory-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4 overflow-auto" data-testid="import-inventory-upload-step">
      {showNumbersHelp && (
        <NumbersFileHelp onDismiss={onDismissNumbersHelp} />
      )}
      <label
        htmlFor="inv-csv-file"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="block border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:bg-muted/30 transition-colors"
      >
        <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <div className="font-medium text-foreground">
          {file ? file.name : "Drop your CSV here or click to browse"}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Accepts .csv · max 5 MB
        </div>
        <input
          id="inv-csv-file"
          type="file"
          accept=".csv,.tsv,.numbers"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="import-inventory-file-input"
        />
      </label>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">No file yet? Grab our starter template with the right headers.</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={downloadTemplate}
          data-testid="import-download-template-btn"
          className="whitespace-nowrap"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download template
        </Button>
      </div>
      <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3 border border-border">
        <div className="font-medium text-foreground mb-1">Tips:</div>
        <ul className="list-disc ml-5 space-y-0.5">
          <li>SKU is the unique identifier — existing SKUs will be UPDATED, not duplicated.</li>
          <li>Missing Quantity, PAR, Cost, or Price default to 0.</li>
          <li>Any new category in the CSV will be created automatically.</li>
          <li>Using Apple Numbers? Export to CSV first — see the steps below.</li>
        </ul>
      </div>
    </div>
  );
}

function NumbersFileHelp({ onDismiss }) {
  const Step = ({ n, icon: Icon, label, detail }) => (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-500/15 text-amber-600 text-xs font-semibold flex items-center justify-center">{n}</div>
      <div className="flex-1">
        <div className="flex items-center gap-1.5 text-sm text-foreground font-medium">
          <Icon className="w-3.5 h-3.5 text-amber-600" /> {label}
        </div>
        {detail && <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>}
      </div>
    </div>
  );
  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
      data-testid="import-numbers-help"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="text-sm font-medium text-foreground">
            Apple Numbers files aren't supported directly
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="import-numbers-help-dismiss"
        >
          Dismiss
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Export your spreadsheet to CSV first, then upload it here. Takes 10 seconds:
      </p>
      <div className="space-y-2.5 pl-1">
        <Step n={1} icon={FolderInput} label="In Numbers, open your file" detail="Click on the Numbers app icon to bring it to the front." />
        <Step n={2} icon={FileText} label="File → Export To → CSV…" detail="From the top menu bar. Keep the default settings." />
        <Step n={3} icon={Save} label="Save the CSV to your Desktop, then drop it above" detail="You'll see the preview + mapping as soon as it uploads." />
      </div>
    </div>
  );
}

function MapStep({
  preview, mapping, updateMapping, defaultCategory, setDefaultCategory,
  isStoreAccount, storeLocations, importLocationId, setImportLocationId,
}) {
  const AVAILABLE = preview.available_fields || [];
  const usedFields = new Set(
    Object.entries(mapping).filter(([, f]) => f !== "ignore").map(([, f]) => f),
  );

  return (
    <div className="flex-1 overflow-auto space-y-3 pr-1" data-testid="import-inventory-map-step">
      {/* Summary */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{preview.total_rows} rows detected in <span className="font-mono">{preview.filename}</span></span>
        <span>{preview.headers.length} columns</span>
      </div>

      {/* Location (multi-location stores only) */}
      {isStoreAccount && (
        <div data-testid="import-location-wrap">
          <label className="text-xs font-medium text-muted-foreground block mb-1">
            Location <span className="text-red-500">*</span>
          </label>
          <Select
            value={importLocationId || "_none_"}
            onValueChange={(v) => setImportLocationId(v === "_none_" ? "" : v)}
          >
            <SelectTrigger className="h-9 max-w-xs" data-testid="import-location-select">
              <SelectValue placeholder="Choose a location…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none_">— Choose a location —</SelectItem>
              {(storeLocations || []).map((loc) => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}{loc.code ? ` (${loc.code})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">
            All imported items will be assigned to this location. SKU upserts match only within this location.
          </p>
        </div>
      )}

      {/* Default category */}
      <div>
        <label className="text-xs font-medium text-muted-foreground block mb-1">
          Default category <span className="font-normal">(optional — used when a row's category column is blank)</span>
        </label>
        <Input
          value={defaultCategory}
          onChange={(e) => setDefaultCategory(e.target.value)}
          placeholder="e.g. Keys & Fobs"
          className="max-w-xs"
          data-testid="import-default-category"
        />
      </div>

      {/* Mapping + preview table */}
      <div className="border border-border rounded-lg overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              {preview.headers.map((h) => {
                const cur = mapping[h] || "ignore";
                const conflict = cur !== "ignore" && Object.entries(mapping).filter(([k, v]) => k !== h && v === cur).length > 0;
                return (
                  <th key={h} className="p-2 align-top min-w-[150px]">
                    <div className="font-medium text-foreground truncate mb-1" title={h}>{h}</div>
                    <Select
                      value={cur}
                      onValueChange={(v) => updateMapping(h, v)}
                    >
                      <SelectTrigger
                        className={`h-8 text-xs ${conflict ? "border-amber-500 text-amber-600" : ""}`}
                        data-testid={`import-map-${h}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE.map((f) => {
                          const disabled = f !== "ignore" && f !== cur && usedFields.has(f);
                          return (
                            <SelectItem key={f} value={f} disabled={disabled}>
                              {FIELD_LABELS[f] || f}{disabled ? " (used)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(preview.preview_rows || []).map((row, i) => (
              <tr key={i} data-testid={`import-preview-row-${i}`}>
                {preview.headers.map((h) => (
                  <td key={h} className="p-2 text-xs text-muted-foreground truncate max-w-[180px]" title={row[h]}>
                    {row[h] || <span className="text-muted-foreground/50">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!Object.values(mapping).some((v) => v === "sku" || v === "name") && (
        <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <span>Map at least SKU or Name/Description to continue.</span>
        </div>
      )}
    </div>
  );
}

function DoneStep({ result, onDownloadErrors }) {
  return (
    <div className="space-y-3 overflow-auto" data-testid="import-inventory-done-step">
      <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4">
        <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
          <CheckCircle2 className="w-5 h-5" /> Import complete
        </div>
        <div className="text-sm text-muted-foreground mt-1" data-testid="import-inventory-result-summary">
          {result.created} created · {result.updated} updated · {result.skipped} skipped · {result.error_count} errors
        </div>
      </div>

      {result.errors?.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-red-700 dark:text-red-400">
              {result.errors.length} row{result.errors.length === 1 ? "" : "s"} failed
            </div>
            <Button size="sm" variant="outline" onClick={onDownloadErrors} data-testid="import-download-errors-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download errors CSV
            </Button>
          </div>
          <ul className="list-disc ml-5 space-y-0.5 text-xs text-muted-foreground max-h-40 overflow-auto">
            {result.errors.slice(0, 20).map((e, i) => (
              <li key={i}>Row {e.row}: {e.reason}</li>
            ))}
            {result.errors.length > 20 && <li>… and {result.errors.length - 20} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
